import { Locale } from '@ardoq/locale';
import { formatInTimeZone } from 'date-fns-tz';
import {
  add,
  Duration,
  format,
  formatISO,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  sub,
  parseISO,
  minutesToMilliseconds,
} from 'date-fns';
import { APIFieldType, PlainDateISO, PlainDateTimeISO } from '@ardoq/api-types';
import { isDateValid, parseLocaleDateString } from './parseUtils';
import {
  currentDate,
  currentDateTimeISO,
  currentTimestamp,
  parseDate,
} from './dateOperations';

/**
 * Locale-invariant English sentinel returned by formatters when the input is
 * not a valid date. Several consumers compare against this exact string
 * (e.g. handsontable datetime editor) or feed it back into parsers, so it
 * MUST remain English. UI layers that need a localized message should call
 * `t(i18nLocale, 'dateTime:invalidDate', 'Invalid date')` themselves.
 */
export const INVALID_DATE_FALLBACK = 'Invalid date';

const dateFromString = (date: string | Date | null, locale: Locale) => {
  if (typeof date === 'string') return parseLocaleDateString(date, locale);

  return date && isDateValid(date) ? date : null;
};

export const isDateBefore = (a: string, b: string, locale: Locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);

  if (!aDate || !bDate) return false;

  return isBefore(aDate, bDate);
};

export const isDateBeforeDay = (a: string, b: string, locale: Locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);

  if (!aDate || !bDate) return false;

  return isBefore(aDate, bDate) && !isSameDay(aDate, bDate);
};

export const isDateSame = (a: string, b: string, locale: Locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);

  if (!aDate || !bDate) return false;

  return isEqual(aDate, bDate);
};

export const isDateSameDay = (a: string, b: string, locale: Locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);

  if (!aDate || !bDate) return false;

  return isSameDay(aDate, bDate);
};

export const isDateAfter = (a: string, b: string, locale: Locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);

  if (!aDate || !bDate) return false;

  return isAfter(aDate, bDate);
};

export const isDateAfterDay = (a: string, b: string, locale: Locale) => {
  const aDate = dateFromString(a, locale);
  const bDate = dateFromString(b, locale);

  if (!aDate || !bDate) return false;

  return isAfter(aDate, bDate) && !isSameDay(aDate, bDate);
};

const methodHash: Record<
  string,
  (date: Date | number, duration: Duration) => Date
> = {
  '+': add,
  '-': sub,
};

const unitHash: Record<string, 'hours' | 'days' | 'months' | 'years'> = {
  h: 'hours',
  d: 'days',
  m: 'months',
  y: 'years',
};

const RELATIVE_DATE_UNIT_SYMBOLS = Object.keys(unitHash).join('');

export const RELATIVE_DATE_REGEX = new RegExp(
  `^now([+-])?(\\d+)?([${RELATIVE_DATE_UNIT_SYMBOLS}])?`
);

const RELATIVE_DATE_TOKEN_REGEX = new RegExp(
  `^now([+-]\\d+[${RELATIVE_DATE_UNIT_SYMBOLS}])?$`
);

export const isRelativeDateToken = (value: string): boolean =>
  RELATIVE_DATE_TOKEN_REGEX.test(value);

export const isHourPrecision = (date: string) => {
  return RELATIVE_DATE_REGEX.test(date) && date?.slice(-1) === 'h';
};

const relativeDateCache = new Map<string, string>();
const CACHE_RESET_INTERVAL = minutesToMilliseconds(5);
let cacheStartTime = currentTimestamp();

/**
 * Returns the ISO string from the relative date calculation.
 * The calculation is cached every 5 minutes, unless a `now` is given
 * explicitly, in which case the cache is bypassed.
 */
export const relativeDateToISOString = (
  relativeDate: string,
  now?: Date
): string => {
  const relativeDateMatch = relativeDate.match(RELATIVE_DATE_REGEX);
  if (!relativeDateMatch) return INVALID_DATE_FALLBACK;
  if (relativeDate === 'now') {
    return now ? now.toISOString() : currentDateTimeISO();
  }

  if (!now) {
    if (currentTimestamp() - cacheStartTime > CACHE_RESET_INTERVAL) {
      relativeDateCache.clear();
      cacheStartTime = currentTimestamp();
    }
    const cacheHit = relativeDateCache.get(relativeDate);
    if (cacheHit) {
      return cacheHit;
    }
  }

  const [, operator, count, unitSymbol] = relativeDateMatch;
  const method = methodHash[operator];
  const unit = unitHash[unitSymbol];
  if (typeof method !== 'function') {
    throw new Error(
      `Unsupported operator encountered while parsing "${relativeDate}"`
    );
  }
  const newDate = method(now ?? currentDate(), { [unit]: +count });
  const isoString = newDate.toISOString();
  if (!now) {
    relativeDateCache.set(relativeDate, isoString);
  }
  return isoString;
};

/**
 * Formats a date with time according to locale.
 * Examples:
 * - English (US): "1/1/2025, 2:30 PM"
 * - English (UK): "01/01/2025, 14:30"
 * - Norwegian: "01.01.2025, 14:30"
 */
export const formatDateTimeShort = (
  date: string | Date | null,
  locale: Locale
) => formatDateByLocaleAndFormatPattern(date, 'P p', locale);

/**
 * Formats a date with an abbreviated month name and time.
 * Examples:
 * - English (US): "Jan 1, 2025 2:30 PM"
 * - English (UK): "1 Jan 2025 14:30"
 * - Norwegian: "1. jan. 2025 14:30"
 */
export const formatDateTimeMedium = (
  date: string | Date | null,
  locale: Locale
) => formatDateByLocaleAndFormatPattern(date, 'PP p', locale);

/**
 * Formats a date.
 * Examples:
 * - English (US): "1/1/2025"
 * - English (UK): "01/01/2025"
 * - Norwegian: "01.01.2025"
 */
export const formatDateShort = (date: string | Date | null, locale: Locale) =>
  formatDateByLocaleAndFormatPattern(date, 'P', locale);

/**
 * Formats a date with an abbreviated month name.
 * Examples:
 * - English (US): "Jan 1, 2025"
 * - English (UK): "1 Jan 2025"
 * - Norwegian: "1. jan. 2025"
 */
export const formatDateMedium = (date: string | Date | null, locale: Locale) =>
  formatDateByLocaleAndFormatPattern(date, 'PP', locale);

/**
 * Formats a date with full month name.
 * Examples:
 * - English (US): "January 1, 2025"
 * - English (UK): "1 January 2025"
 * - Norwegian: "1. januar 2025"
 */
export const formatDateLong = (date: string | Date | null, locale: Locale) =>
  formatDateByLocaleAndFormatPattern(date, 'PPP', locale);

/**
 * Formats a date with full month name and year.
 * Examples:
 * - English (US): "January 2025"
 * - English (UK): "January 2025"
 * - Norwegian: "januar 2025"
 */
export const formatDateMonthYear = (
  date: string | Date | null,
  locale: Locale
) => formatDateByLocaleAndFormatPattern(date, 'MMMM yyyy', locale);

export const formatDateMonthYearUTC = (
  date: string | Date | null,
  locale: Locale
): string =>
  formatDateByLocaleAndFormatPattern(date, 'MMMM yyyy', locale, 'UTC');

export const formatDateISO = (date: Date | null) => {
  if (!date) return '';
  if (!isDateValid(date)) return INVALID_DATE_FALLBACK;

  return formatISO(date, { representation: 'date' });
};

export const formatDateISOIfDate = (
  date: string | Date | null
): string | null => (date instanceof Date ? formatDateISO(date) : date);

const formatDateByLocaleAndFormatPattern = (
  dateOrDateString: string | Date | null,
  dateFormat: string,
  locale: Locale,
  timeZone?: string
): string => {
  if (!dateOrDateString) return '';

  const date = dateFromString(dateOrDateString, locale);

  if (!date) return INVALID_DATE_FALLBACK;

  if (timeZone) return formatInTimeZone(date, timeZone, dateFormat, { locale });

  return format(date, dateFormat, { locale });
};

export const isISO8601DateOnly = (value: string): value is PlainDateISO =>
  formatDateISO(parseISO(value)) === value;

export const isISO8601DateTime = (value: string): value is PlainDateTimeISO => {
  const date = parseDate(value);
  if (date.toString() === 'Invalid Date') return false;
  return date.toISOString() === value;
};

const timeFormatCache = new Map<Locale, boolean>();

export const isCurrentLocaleTimeFormat12Hour = (locale: Locale) => {
  if (timeFormatCache.has(locale)) return timeFormatCache.get(locale)!;

  const is12HourTime =
    new Intl.DateTimeFormat(locale.code).resolvedOptions().hour12 ?? false;

  timeFormatCache.set(locale, is12HourTime);

  return is12HourTime;
};

export const isDateFieldType = (
  fieldType: unknown
): fieldType is APIFieldType =>
  fieldType === APIFieldType.DATE_ONLY || fieldType === APIFieldType.DATE_TIME;
